import { sum } from 'lodash';
import { baseApi } from '../../app/baseApi';
import { apiTags } from '../../app/store/apiTags';
import { arrayToObject } from 'utils/object-util';
import { loadStatuses } from './enums';


const getCacheKey = (args) => {
    return `${args.groupId}`
}

const apiPrefix = 'load';
const convertLoad = (load, lotLookup) => {
    return ({
        ...load,
        vinsForSearch: load?.assets?.map(a => a.vin).join('$$'),
        latestStatusObject: load?.statuses?.[0] ?? {},
        latestStatus: load?.statuses?.[0]?.status ?? {},
        latestStatusDate: load?.statuses?.[0]?.statusDate ?? {},
        actualPickupDate: load?.statuses?.find(s => s.status == loadStatuses.TRANSIT)?.statusDate,
        actualDeliveryDate: load?.statuses?.find(s => s.status == loadStatuses.DELIVERED)?.statusDate,
        acceptedDate: load?.statuses?.find(s => s.status == loadStatuses.ACCEPTED)?.statusDate,
        carrierId: load.carrier?.transporterId,
        readyForPosting: true,
        pickupLocation: lotLookup?.[load?.pickupLocationId],
        deliveryLocation: lotLookup?.[load?.deliveryLocationId],
        finalDeliveryDate: load?.deliveryEndDate,
    })
}

const convertGroupLoadsList = (group, lotLookup) => {
    return group?.loads?.map((l, index) => {
        const load = convertLoad(l, lotLookup);
        load.order = index + 1;
        const prevLoadDelivered = index > 0 ? group?.loads?.[index - 1]?.isInTransit || group?.loads?.[index - 1]?.isComplete : true;
        load.readyForPosting = !!prevLoadDelivered;
        return load;
    });
}

export const loadApi = baseApi.injectEndpoints({
    reducerPath: 'loadApi',
    tagTypes: [apiTags.LOADS, apiTags.LOAD_GROUP],
    endpoints: (builder) => ({
        getLoads: builder.query({
            query: (body) => ({
                url: body?.lmStatus ? `${apiPrefix}?archived=${body?.archived ? true : false}&lmStatus=${body?.lmStatus}` : `load`
            }),
            providesTags: [apiTags.LOADS],
            transformResponse: (response) => {
                return response.map(load => convertLoad(load))
            }
        }),
        getLoadsGrouped: builder.query({
            query: (body) => ({
                url: `${apiPrefix}/grouped?archived=${body?.archived ? true : false}&${body?.lmStatus ? `lmStatus=${body?.lmStatus}` : ""}`
            }),
            providesTags: [apiTags.LOADS],
            transformResponse: (response) => {
                const lotLookup = arrayToObject(response.lots, 'id');
                return response.groups.map((group) => {
                    const loads = convertGroupLoadsList(group, lotLookup);
                    group.statuses = loads?.flatMap(s => s.statuses);
                    return ({
                        ...(loads?.[0] ?? {}),//get fields like external id, move type etc
                        ...convertLoad(group, lotLookup),
                        miles: sum(loads?.map(l => l.miles)),
                        loads: loads,
                        loadBoardIdForSearch: loads?.map(l => l.loadBoardId).join("$$"),
                        loadLegIdForSearch: loads?.map(l => l.id).join("$$")
                    })
                })
            }
        }),
        getLoadGroup: builder.query({
            query: (groupId) => ({
                url: `${apiPrefix}/grouped/${groupId}`
            }),
            //providesTags: [apiTags.LOAD_GROUP],
            providesTags: (result, error, arg) => [{ type: apiTags.LOAD_GROUP, id: getCacheKey({ groupId: arg }) }],
            transformResponse: (response) => {
                const group = response?.groups?.[0];
                const lotLookup = arrayToObject(response.lots, 'id');
                const loads = convertGroupLoadsList(group, lotLookup);
                group.statuses = loads?.flatMap(s => s.statuses);
                return ({
                    ...(loads?.[0] ?? {}),//get fields like external id, move type etc
                    ...convertLoad(group, lotLookup),
                    miles: sum(loads?.map(l => l.miles)),
                    loads: loads
                })
            }
        }),
        getLoadLocations: builder.query({
            query: (loadID) => `${apiPrefix}/history/${loadID}`,
        }),
        getLoadGroupLocations: builder.query({
            query: (groupId) => `${apiPrefix}/group/${groupId}/history`,
        }),
        getLoadAttachments: builder.query({
            query: (loadID) => `${apiPrefix}/attachments/${loadID}`,
        }),
        getLoadGroupAttachments: builder.query({
            query: (groupId) => `${apiPrefix}/group/${groupId}/attachments`,
        }),
        getLoadPayments: builder.query({
            query: (loadID) => `${apiPrefix}/payments/${loadID}`,
        }),
        getLoadGroupPayments: builder.query({
            query: (groupId) => `${apiPrefix}/group/${groupId}/payments`,
        }),
        importLoads: builder.mutation({
            query: form => ({
                url: `${apiPrefix}/import`,
                method: "POST",
                body: form
            }),
            invalidatesTags: [apiTags.LOADS]
        }),
        dispatchLoad: builder.mutation({
            query: ({ loadId, transporterId, groupId }) => ({
                url: `${apiPrefix}/dispatch`,
                method: "POST",
                body: {
                    loadId,
                    transporterId
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey(arg) },
                apiTags.LOADS,
                apiTags.LOAD_GROUP
            ],
            //invalidatesTags: [apiTags.LOADS, apiTags.LOAD_GROUP, ]
        }),
        postLoad: builder.mutation({
            query: (body) => ({
                url: `${apiPrefix}/post`,
                method: "POST",
                body
            }),
            invalidatesTags: (result, error, arg) => [
                ...(arg?.[0]?.groupIds?.map(g => ({ type: apiTags.LOAD_GROUP, id: getCacheKey({ groupId: g }) })) ?? []),
                apiTags.LOADS,
                apiTags.LOAD_GROUP
            ],
        }),
        addStatus: builder.mutation({
            query: ({ status, statusDate, comment, loadIds, groupIds, reasonId,subReasonId }) => ({
                url: `${apiPrefix}/status`,
                method: "POST",
                body: {
                    status,
                    statusDate,
                    comment,
                    loadIds,
                    reasonId,
                    subReasonId
                }
            }),
            invalidatesTags: (result, error, arg) => [
                ...(arg.groupIds?.map(g => ({ type: apiTags.LOAD_GROUP, id: getCacheKey({ groupId: g }) })) ?? []),
                apiTags.LOADS,
                apiTags.VEHICLES
            ],
            //invalidatesTags: [apiTags.LOADS, apiTags.VEHICLES, apiTags.LOAD_GROUP]
        }),
        updateStatus: builder.mutation({
            query: ({ id, status, statusDate, comment, loadId, groupId, reasonId, subReasonId }) => ({
                url: `${apiPrefix}/status/edit`,
                method: "POST",
                body: {
                    id,
                    status,
                    statusDate,
                    comment,
                    loadId,
                    reasonId,
                    subReasonId
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey(arg) },
                apiTags.LOADS
            ],
            //invalidatesTags: [apiTags.LOADS, apiTags.LOAD_GROUP]
        }),
        deleteStatus: builder.mutation({
            query: ({ id, groupId }) => ({
                url: `${apiPrefix}/status/delete`,
                method: "POST",
                body: { id }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey(arg) },
                apiTags.LOADS
            ],
            //invalidatesTags: [apiTags.LOADS, apiTags.LOAD_GROUP]
        }),
        cancelLoad: builder.mutation({
            query: ({ loadId, comment, groupId, reasonId }) => ({
                url: `${apiPrefix}/cancel`,
                method: "POST",
                body: {
                    loadId,
                    comment,
                    groupId,
                    reasonId
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey(arg) },
                apiTags.LOADS
            ],
            //invalidatesTags: [apiTags.LOADS, apiTags.LOAD_GROUP]
        }),
        updateActualDate: builder.mutation({
            query: (body) => ({
                url: `${apiPrefix}/updateschedule`,
                method: "POST",
                body: body
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey(arg) },
                apiTags.LOADS
            ],
            //invalidatesTags: [apiTags.LOADS, apiTags.LOAD_GROUP]
        }),
        getLoadsDashboard: builder.query({
            query: (params) => {
                const queryString = Object.keys(params).map(param => params[param] ? `${param}=${params[param]}` : undefined).filter(k => k).join('&');
                return `${apiPrefix}/dashboard?${queryString}`
            }
        }),
        getGroupedLoadsDashboard: builder.query({
            query: (params) => {
                const queryString = Object.keys(params).map(param => params[param] ? `${param}=${params[param]}` : undefined).filter(k => k).join('&');
                return `${apiPrefix}/groupedDashboard?${queryString}`
            }
        }),
        addLoad: builder.mutation({
            query: load => ({
                url: `${apiPrefix}/add`,
                method: "POST",
                body: load
            }),
            invalidatesTags: [apiTags.LOADS, apiTags.VEHICLES, apiTags.LOTS, apiTags.CLIENT_CONFIG]
        }),
        // addLoadLeg: builder.mutation({
        //     query: load => ({
        //         url: `${apiPrefix}/addLeg`,
        //         method: "POST",
        //         body: load
        //     }),
        //     invalidatesTags: (result, error, arg) => [
        //         { type: apiTags.LOAD_GROUP, id: getCacheKey(arg) },
        //         apiTags.LOADS,
        //         apiTags.LOTS,
        //         apiTags.CLIENT_CONFIG
        //     ],
        //     //invalidatesTags: [apiTags.LOADS, apiTags.LOAD_GROUP, apiTags.LOTS, apiTags.CLIENT_CONFIG]
        // }),
        updateLoad: builder.mutation({
            query: load => ({
                url: `${apiPrefix}/update`,
                method: "POST",
                body: load
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey(arg) },
                apiTags.LOADS,
                apiTags.LOTS,
                apiTags.CLIENT_CONFIG
            ],
            //invalidatesTags: [apiTags.LOADS, apiTags.LOTS, apiTags.CLIENT_CONFIG, apiTags.LOAD_GROUP]
        }),
        getImportLoadTemplate: builder.mutation({
            query: ({ clientAccountId, multipleVehicles = false })  => ({
                url: `${apiPrefix}/downloadtemplate`,
                method: "POST",
                body: { clientAccountId, multipleVehicles },
                responseHandler: async (response) => window.location.assign(window.URL.createObjectURL(await response.blob())),
                cache: "no-cache"
            })
        }),
        addLoadGroup: builder.mutation({
            query: group => ({
                url: "load/addGroup",
                method: "POST",
                body: group
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey({ groupId: arg.id }) },
                apiTags.LOADS, apiTags.VEHICLES, apiTags.LOTS, apiTags.CLIENT_CONFIG, apiTags.VEHICLE,
                // Also invalidated on error: a rejected create may mean the rubric price went stale
                apiTags.RUBRIC_PRICE_QUERY
            ],
        }),
        addActivityToLoad: builder.mutation({
            query: ({ loadGroupId, activityItemId, activityTypeId, lotId, activityDate, vendorId, vehicleId, workorderCustomData }) => ({
                url: `${apiPrefix}/addActivity`,
                method: "POST",
                body: {
                    loadGroupId,
                    activityItemId,
                    activityTypeId,
                    lotId,
                    activityDate,
                    vendorId,
                    vehicleId,
                    workorderCustomData
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey({ ...arg, groupId: arg.loadGroupId }) },
                apiTags.LOADS
            ],
        }),
        deleteActivityFromLoad: builder.mutation({
            query: ({ loadGroupId, activityGroupId }) => ({
                url: `${apiPrefix}/deleteActivity`,
                method: "POST",
                body: {
                    loadGroupId,
                    activityGroupId
                }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey({ ...arg, groupId: arg.loadGroupId }) },
                apiTags.LOADS
            ],
        }),
        getLoadStatusReasons: builder.query({
            query: () => `${apiPrefix}/reasons`,
        }),
        getLoadsStatusDashboard: builder.query({
            query: (params) => {
                const queryString = Object.keys(params).map(param => params[param] ? `${param}=${params[param]}` : undefined).filter(k => k).join('&');
                return `${apiPrefix}/statusDashboard?${queryString}`
            },
            transformResponse: (response) => {
                return response.map(load => convertLoad(load))
            }
        }),
        editLoadPrice: builder.mutation({
            query: ({ loadId, charge, cost, status, loadGroupId }) => ({
                url: `${apiPrefix}/updatePricing`,
                method: "POST",
                body: { loadId, charge, cost, status }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey({ ...arg, groupId: arg?.loadGroupId }) },
                apiTags.LOADS
            ],
        }),
        approveLoadPrice: builder.mutation({
            query: ({ id, status, loadId, loadGroupId }) => ({
                url: `${apiPrefix}/approvePricing`,
                method: "POST",
                body: { id, status, loadId }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: apiTags.LOAD_GROUP, id: getCacheKey({ ...arg, groupId: arg?.loadGroupId }) },
                apiTags.LOADS
            ],
        }),
    })
});

export const {
    useGetLoadsQuery,
    useGetLoadLocationsQuery,
    useGetLoadAttachmentsQuery,
    useGetLoadPaymentsQuery,
    useGetLoadGroupPaymentsQuery,
    useImportLoadsMutation,
    useDispatchLoadMutation,
    useCancelLoadMutation,
    useGetLoadsDashboardQuery,
    useGetGroupedLoadsDashboardQuery,
    useUpdateStatusMutation,
    useAddStatusMutation,
    useDeleteStatusMutation,
    useUpdateActualDateMutation,
    usePostLoadMutation,
    useAddLoadMutation,
    useUpdateLoadMutation,
    useGetImportLoadTemplateMutation,
    useGetLoadsGroupedQuery,
    useGetLoadGroupLocationsQuery,
    useGetLoadGroupAttachmentsQuery,
    useGetLoadGroupQuery,
    useAddLoadGroupMutation,
    useAddActivityToLoadMutation,
    useDeleteActivityFromLoadMutation,
    useGetLoadStatusReasonsQuery,
    useGetLoadsStatusDashboardQuery,
    useEditLoadPriceMutation,
    useApproveLoadPriceMutation
} = loadApi;
